import React, { useCallback, useEffect, useRef, useState } from "react";
import PublicSidebar from "./PublicSidebar";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/useAuth";
import "./PublicSite.css";

const PublicMenu: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const desktopNav = useRef<HTMLElement>(null);
  const { login } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const closeDesktopMenus = useCallback(() => {
    desktopNav.current?.querySelectorAll<HTMLDetailsElement>("details[open]")
      .forEach(menu => { menu.open = false; });
  }, []);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (!desktopNav.current?.contains(event.target as Node)) closeDesktopMenus();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      const activeMenu = desktopNav.current?.querySelector<HTMLDetailsElement>("details[open]");
      if (!activeMenu) return;
      event.preventDefault();
      closeDesktopMenus();
      activeMenu.querySelector("summary")?.focus();
    };
    document.addEventListener("click", onClick);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("click", onClick);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [closeDesktopMenus]);

  useEffect(() => {
    if (!window.matchMedia) return;
    const desktop = window.matchMedia("(min-width: 1100px)");
    const onChange = () => { if (desktop.matches) setOpen(false); };
    desktop.addEventListener("change", onChange);
    return () => desktop.removeEventListener("change", onChange);
  }, []);

  const handleLogin = async () => {
    closeDesktopMenus();
    setLoginError("");
    setLoggingIn(true);
    try {
      await login();
      navigate(pathname.startsWith("/carpe") ? "/carpe/dashboard" : "/dashboard");
      setOpen(false);
    } catch {
      setLoginError("Accesso non riuscito. Riprova.");
    } finally { setLoggingIn(false); }
  };

  const navigationGroup = (label: string, links: [string, string][]) => (
    <details className="public-nav-group" onToggle={event => {
      const current = event.currentTarget;
      if (current.open) desktopNav.current?.querySelectorAll<HTMLDetailsElement>("details[open]")
        .forEach(menu => { if (menu !== current) menu.open = false; });
    }}>
      <summary>
        {label}
        <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="m6 9 6 6 6-6" /></svg>
      </summary>
      <div className="public-nav-group__links">
        {links.map(([text, route]) => <NavLink key={route} to={route} onClick={closeDesktopMenus}>{text}</NavLink>)}
      </div>
    </details>
  );
  return (
    <header className="public-header">
      <div className="public-header__inner">
        <Link to="/" className="public-brand" aria-label="ASD Parco della Sciola, home">
          <img src="/images/logo.png" alt="" width="50" height="70" />
          <strong>Parco della Sciola</strong>
        </Link>
        <nav ref={desktopNav} className="public-desktop-links" aria-label="Collegamenti principali">
          {navigationGroup("Il parco", [["Chi siamo", "/chi-siamo"], ["Dove siamo", "/dove-siamo"]])}
          {navigationGroup("Trout Area", [["Calendario gare", "/gare"], ["Campionati", "/campionati"], ["Statistiche", "/statistiche"]])}
          {navigationGroup("Carpe e Carassi", [["Calendario gare", "/carpe/gare"], ["Campionati", "/carpe/campionati"], ["Statistiche", "/carpe/statistiche"]])}
          <NavLink to="/contatti" onClick={closeDesktopMenus}>Contatti</NavLink>
          <button type="button" className="public-access-button" onClick={handleLogin} disabled={loggingIn}>
            {loggingIn ? "Accesso in corso…" : "Area riservata"}
          </button>
        </nav>
        <button
          type="button"
          className="public-menu-button"
          onClick={() => setOpen(true)}
          aria-label="Apri menu completo"
          aria-expanded={open}
          aria-controls={open ? "public-navigation" : undefined}
        >
          Menu
          <svg
            aria-hidden="true"
            width="20"
            height="20"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
        </button>
      </div>
      {!open && loginError && <p role="alert" className="public-login-error">{loginError}</p>}
      <PublicSidebar open={open} onClose={() => setOpen(false)} onLogin={handleLogin} loggingIn={loggingIn} loginError={loginError} />
    </header>
  );
};

export default PublicMenu;
